import { Button } from '@playcanvas/pcui/react';
import React from 'react';

import AnimationControls from './animation-controls';
import { FragmentPanel, MeasurementsPanel, ViewPanel, InfoPanel } from './panels';
import { addEventListenerOnClickOnly, isMobileLayout } from '../../helpers';
import { t } from '../../i18n/translations';
import { SetProperty, ObserverData } from '../../types';

const PopupPanelControls = (props: { observerData: ObserverData, setProperty: SetProperty }) => {
    return (<>
        <InfoPanel setProperty={props.setProperty} observerData={props.observerData} />
        <MeasurementsPanel setProperty={props.setProperty} observerData={props.observerData} />
        <FragmentPanel setProperty={props.setProperty} observerData={props.observerData} />
        <ViewPanel setProperty={props.setProperty} sceneData={props.observerData.scene} uiData={props.observerData.ui} runtimeData={props.observerData.runtime}/>
    </>);
};

class PopupButtonControls extends React.Component <{ observerData: ObserverData, setProperty: SetProperty }> {
    popupPanelElement: HTMLElement | null = null;

    removeDeselectEvents: (() => void) | null = null;

    /** Панель, под которую сейчас стоит подписка на «клик мимо»: без неё подписались бы дважды. */
    armedFor: string | null = null;

    componentDidUpdate() {
        // Панель открывает не только тулбар: двойной клик по боксу фрагмента возвращает её
        // прямо из вьюера, минуя обработчик кнопки. Без этой досылки такая панель осталась бы
        // висеть — закрыть её кликом мимо было бы нечем.
        const active = this.props.observerData.ui.active;
        if (this.armedFor !== active) {
            this.armDeselect(active);
        }
    }

    componentWillUnmount() {
        this.removeDeselectEvents?.();
        this.removeDeselectEvents = null;
    }

    private handleClick = (value: string) => {
        const nextActive = this.props.observerData.ui.active === value ? null : value;
        this.props.setProperty('ui.active', nextActive);
        this.armDeselect(nextActive);
    };

    /**
     * Переподписаться на закрытие панели кликом мимо неё.
     *
     * @param active - Панель, которая теперь открыта, либо `null`, если открытой нет.
     */
    private armDeselect = (active: string | null) => {
        this.armedFor = active;
        if (this.removeDeselectEvents) {
            this.removeDeselectEvents();
            this.removeDeselectEvents = null;
        }

        if (!active) return;

        if (!this.popupPanelElement) this.popupPanelElement = document.getElementById('popup');

        setTimeout(() => {
            const deactivateUi = (e: MouseEvent) => {
                const target = e.target;
                if (target instanceof Node && this.popupPanelElement?.contains(target)) {
                    return;
                }
                // Куб ориентации и переключатель проекции лежат поверх канваса, но это
                // инструменты просмотра, а не «клик мимо панели»: закрывать по ним панель
                // нельзя — вместе с ней исчезли бы они сами.
                if (target instanceof Element && target.closest('#view-cube-container, .projection-toggle-slot')) {
                    return;
                }
                // Choosing a fragment is intentionally a click on the canvas outside
                // the popup. Keep the panel and selection mode alive for that click.
                if (document.querySelector('.fragment-panel.fragment-selecting') && target instanceof HTMLCanvasElement) {
                    return;
                }
                // Точки измерения ставят кликом по модели. Для этой панели клик по канвасу —
                // её собственный рабочий жест, а не «мимо»: закрыв её, мы бы вместе с ней
                // выключили инструмент и стёрли из виду результат.
                if (this.props.observerData.ui.active === 'measurement' &&
                    (target instanceof HTMLCanvasElement ||
                     (target instanceof Element && target.closest('.measure-overlay')))) {
                    return;
                }
                this.props.setProperty('ui.active', null);
                this.removeDeselectEvents?.();
                this.removeDeselectEvents = null;
            };
            this.removeDeselectEvents = addEventListenerOnClickOnly(document.body, deactivateUi, 4);
        });
    };

    render() {
        const buildClass = (value: string) => {
            return (this.props.observerData.ui.active === value) ? ['popup-button', 'selected'] : ['popup-button'];
        };

        const lang = this.props.observerData?.ui?.language;
        const embed = this.props.observerData?.ui?.embed;
        // Вне встройки — всегда; во встройке — по флагу animControls (его дефолт
        // зависит от пресета: full/compact → вкл, minimal → выкл).
        const showAnimationControls = (!(embed?.enabled) || embed?.animControls !== false) &&
            !(isMobileLayout() && (
                this.props.observerData.fragment.selecting ||
                this.props.observerData.fragment.initialized ||
                this.props.observerData.fragment.enabled
            ));
        const showInfoButton = !(embed?.enabled) || embed.info || embed.controls;
        // Измерения требуют точных кликов по поверхности и чтения чисел в панели — на
        // телефоне это нерабочий сценарий, поэтому инструмент туда не выносится.
        const showMeasureButton = (!(embed?.enabled) || embed.measure) && !isMobileLayout();
        const showFitButton = !(embed?.enabled) || embed.fit;
        const showFullscreenButton = !(embed?.enabled) || embed.fullscreen;
        const showHdButton = !(embed?.enabled) || embed.hd;
        const showShareButton = !(embed?.enabled) || embed.share;
        const showCameraModeButton = !(embed?.enabled) || embed.cameraMode;
        const showFragmentButton = !!this.props.observerData.scene.isTileset && (!(embed?.enabled) || embed.fragment);
        const fragmentActive = this.props.observerData.fragment.selecting ||
            this.props.observerData.fragment.initialized ||
            this.props.observerData.fragment.enabled;
        const wrap = (titleText: string, btn: React.ReactNode) => (
            <span title={titleText} style={{ display: 'contents' }}>{btn}</span>
        );
        return (
            <div id='popup-buttons-parent'>
                {showAnimationControls && (
                    <AnimationControls animationData={this.props.observerData.animation} setProperty={this.props.setProperty} lang={this.props.observerData?.ui?.language} />
                )}
                {showInfoButton && wrap(t('Info', lang), (
                    <Button
                        class={buildClass('info').concat('info-button')}
                        id='info-button'
                        width={40}
                        height={40}
                        onClick={() => this.handleClick('info')}
                    />
                ))}
                {showHdButton && wrap(this.props.observerData.camera.hq ? t('HD mode', lang) : t('SD mode', lang), (
                    <Button
                        class={['popup-button', 'hd-button', this.props.observerData.camera.hq ? 'hd-mode' : 'sd-mode']}
                        id='hd-button'
                        width={40}
                        height={40}
                        onClick={() => {
                            this.props.setProperty('camera.hq', !this.props.observerData.camera.hq);
                        }}
                    />
                ))}
                {showHdButton && this.props.observerData.runtime.hdrSource && wrap(t(
                    this.props.observerData.runtime.hdrAvailable ?
                        (this.props.observerData.runtime.hdrActive ? 'HDR output: on. Click for SDR preview.' : 'SDR preview: click for HDR output.') :
                        'HDR output is unavailable in this session. The HDR texture is shown in SDR.', lang), (
                    <Button text='HDR' id='hdr-button' width={40} height={40}
                        class={['popup-button', 'hdr-button', this.props.observerData.runtime.hdrActive ? 'hdr-active' : 'hdr-inactive']}
                        enabled={this.props.observerData.runtime.hdrAvailable === true}
                        onClick={() => this.props.setProperty('runtime.hdrRequested', !this.props.observerData.runtime.hdrActive)} />
                ))}
                {showMeasureButton && wrap(t('Measurement', lang), (
                    <Button
                        class={buildClass('measurement')
                        .concat('measurement-button')
                        .concat(this.props.observerData.measure.enabled ? 'measure-enabled' : [])}
                        id='measurement-button'
                        width={40}
                        height={40}
                        onClick={() => {
                            const isOpening = this.props.observerData.ui.active !== 'measurement';
                            this.handleClick('measurement');
                            if (isOpening) {
                                this.props.setProperty('measure.enabled', true);
                            }
                        }}
                    />
                ))}
                {showFragmentButton && wrap(t('Fragment view', lang), (
                    <Button
                        class={buildClass('fragment')
                        .concat('fragment-button')
                        .concat(fragmentActive ? 'fragment-enabled' : [])}
                        id='fragment-button'
                        width={40}
                        height={40}
                        onClick={() => {
                            const isOpening = this.props.observerData.ui.active !== 'fragment';
                            this.handleClick('fragment');
                            if (isOpening && !this.props.observerData.fragment.initialized) {
                                window.viewer?.beginFragmentSelection?.();
                            }
                        }}
                    />
                ))}
                {showShareButton && wrap(t('View & share', lang), (
                    <Button class={buildClass('view').concat('view-button')} id='view-button' width={40} height={40} onClick={() => this.handleClick('view')} />
                ))}
                {showFitButton && wrap(t('Frame scene', lang), (
                    <Button
                        class={['popup-button', 'fit-screen-button']}
                        id='fit-screen-button'
                        width={40}
                        height={40}
                        onClick={() => window.viewer?.frameScene?.()}
                    />
                ))}
                {showCameraModeButton && wrap(t({
                    orbit: 'Orbit mode',
                    fly: 'Fly mode',
                    walk: 'Walk mode'
                }[this.props.observerData.camera.mode] ?? 'Orbit mode', lang), (
                    <Button
                        class={['popup-button', 'camera-mode-button', ...(this.props.observerData.camera.mode ? [this.props.observerData.camera.mode] : [])]}
                        id='camera-mode-button'
                        width={40}
                        height={40}
                        onClick={() => {
                            const modes = ['orbit', 'fly', 'walk'] as const;
                            const current = modes.indexOf(this.props.observerData.camera.mode);
                            const mode = modes[(current + 1) % modes.length];
                            this.props.setProperty('camera.mode', mode);
                        }}
                    />
                ))}
                {showFullscreenButton && wrap(this.props.observerData.ui.fullscreen ? t('Exit fullscreen', lang) : t('Fullscreen', lang), (
                    <Button
                        class={['popup-button', 'fullscreen-button', this.props.observerData.ui.fullscreen ? 'fullscreen-exit' : 'fullscreen-enter']}
                        id='fullscreen-button'
                        width={40}
                        height={40}
                        onClick={() => {
                            const el = document.getElementById('application-container');
                            if (!el) return;
                            if (document.fullscreenElement) {
                                document.exitFullscreen?.();
                            } else {
                                el.requestFullscreen?.();
                            }
                        }}
                    />
                ))}
            </div>
        );
    }
}

class PopupPanel extends React.Component <{ observerData: ObserverData, setProperty: SetProperty }> {
    render() {
        return (<div id='popup' className={this.props.observerData.scene.nodes === '[]' ? 'empty' : null}>
            <PopupPanelControls observerData={this.props.observerData} setProperty={this.props.setProperty} />
            <PopupButtonControls observerData={this.props.observerData} setProperty={this.props.setProperty} />
        </div>);
    }
}

export default PopupPanel;
